<template>
  <div>
    <b-row>
      <b-col cols="4">
        <card-list-info header="Order Detail" :items="orderDetail"></card-list-info>
      </b-col>
      <b-col cols="8" v-if="confirmed">
        <b-row v-for="confirmation in confirmationsData" :key="confirmation">
          <b-col>
            <card-list-info :header="confirmation.confirmation.title" :items="confirmation.confirmation.infoList">
              <template slot="signature" slot-scope={data}>Signature :
                <img class="confirm-signature" :src="createSignature(data.value)" alt="signature">
              </template>
            </card-list-info>
          </b-col>
          <b-col>
            <card-list-info :header="confirmation.notes.title" :items="confirmation.notes.infoList">
              <template slot="images" slot-scope={data}>
                <b-carousel
                  style="text-shadow: 1px 1px 2px #333;"
                  controls
                  indicators
                  background="#ababab"
                  :interval="4000"
                  v-model="slide"
                >
                  <b-carousel-slide v-for="image in data.value" :key="image">
                    <img
                      slot="img"
                      class="d-block img-fluid mx-auto img-notes"
                      :src="`${image}`"
                      @click="showThumbnail(data.value)"
                    />
                  </b-carousel-slide>
                </b-carousel>
                <div class="images" v-viewer="options" hidden ref="viewer">
                  <img v-for="image in data.value" :src="image" :key="`${uniqueId('image_')}`">
                </div>
              </template>
            </card-list-info>
          </b-col>
        </b-row>
      </b-col>
    </b-row>
    <b-row v-if="validDestination">
      <b-col>
        <card-list-info header="Destination" :items="destinationInfo">
          <template slot="map" slot-scope={data}>
            <b-btn v-b-toggle.map size="sm" variant="primary" @click="showHideMap">
              {{ !showMap ? 'Show Map' : 'Hide Map' }}
            </b-btn>
            <b-collapse id="map" class="mt-2">
              <base-map
                ref="map"
                :marker="data.value.marker"
                :center="data.value.center"
                :popup="data.value.name"
                :coordinates="data.value.coordinates"
                :radius="data.value.radius"
                :isCircle="data.value.isCircle"
                :latlngs="data.value.latlngs">
              </base-map>
            </b-collapse>
          </template>
        </card-list-info>
        </b-col>
    </b-row>
  </div>
</template>
<script>
import { isEmpty, isObject, isUndefined, isNull, find, values, get } from 'lodash';

import uniqueId from 'lodash/uniqueId';

import L from 'leaflet';
import moment from 'moment';

import dateMixin from '@/mixins/date';
import CardListInfo from '@/components/CardListInfo';
import BaseMap from '@/components/BaseMap';
import * as Config from '@/config';

export default {
  mixins: [dateMixin],
  data() {
    return {
      slide: 0,
      detailData: null,
      showMap: false,
      mapObject: {},
      setInvalidateSize: false,
      options: {
        inline: false,
        button: false,
        navbar: true,
        title: false,
        toolbar: true,
        tooltip: false,
        movable: false,
        zoomable: false,
        rotatable: false,
        scalable: false,
        transition: true,
        fullscreen: true,
        keyboard: true,
        url: 'data-source',
      },
    };
  },
  components: {
    CardListInfo,
    BaseMap,
  },
  mounted() {
    this.$nextTick(() => {
      setTimeout(() => { this.mapObject = this.$refs.map.$refs.map.mapObject; }, 5e2);
    });
  },
  computed: {
    detailedInfo() {
      return this.$store.getters['shipment_md/orders/get_detail_data'];
    },
    headerConfirmation() {
      return this.detailedInfo.status === 'REJECTED' ? 'Rejected' : 'Confirmation';
    },
    headerNotes() {
      return this.detailedInfo.status === 'REJECTED' ? 'Reason' : 'Notes';
    },
    confirmationData() {
      let infoList = [];
      const { status } = this.detailedInfo;

      if (status === 'REJECTED') {
        const metadata = get(this.detailedInfo, 'metadata', {});
        const rejected = get(metadata, 'rejected', {});
        const datetime = moment(rejected.datetime).format('D MMM YYYY HH:mm');
        infoList = [
          { key: 'confirmed_at', label: 'Rejected at', value: datetime },
          { key: 'customer', label: 'Reject by', value: rejected.name },
        ];
      } else {
        const data = this.detailedInfo.confirmations
          .find(c => c.key === 'customer');

        if (!data) { return []; }

        infoList = [
          { key: 'confirmed_at', label: 'Confirmed at', value: this.formatDateTime(data.created_at) },
          { key: 'customer', label: 'Receiver', value: data.name },
        ];

        if (!isEmpty(data.signature)) {
          infoList.push({ key: 'signature', label: 'Signature', value: data.signature });
        }
      }

      return infoList;
    },
    confirmationsData() {
      let confirmationList = [];
      const { status } = this.detailedInfo;

      if (status === 'REJECTED' || status === 'CANCELLED') {
        const confirmations = get(this.detailedInfo, 'confirmations', []);
        const metadata = get(this.detailedInfo, 'metadata', {});
        const rejectionKey = Object.prototype.hasOwnProperty.call(metadata, 'rejected') ? 'rejected' : 'abort';
        const rejected = get(metadata, rejectionKey, {});
        const datetime = moment(rejected.datetime).format('D MMM YYYY HH:mm');
        const confirmationDt = find(confirmations, { key: 'customer' });
        const confirmationMetadata = get(confirmationDt, 'metadata', {});
        const images = get(confirmationMetadata, 'images', []);

        const rejectedConfirmation = {
          confirmation: {
            title: 'Rejected',
            infoList: [
              { key: 'confirmed_at', label: 'Rejected at', value: datetime },
              { key: 'customer', label: 'Reject by', value: rejected.name },
            ],
          },
          notes: {
            title: 'Reason',
            infoList: [
              { key: 'notes', label: 'Notes', value: get(rejected, 'reason', '-') },
            ],
          },
        };

        if (!isEmpty(images)) {
          const imagesDt = images.map((image) => {
            const formattedImage = this.generateImgUrl(image);
            return formattedImage;
          });

          rejectedConfirmation.notes.infoList.push({ key: 'images', value: imagesDt });
        }

        confirmationList.push(rejectedConfirmation);
      } else {
        const confirmations = get(this.detailedInfo, 'confirmations', []);

        confirmationList = confirmations.map((data) => {
          const confirmationItem = {
            confirmation: {
              title: data.key === 'customer' ? 'Confirmation' : 'Manufactur Confirmation',
              infoList: [
                { key: 'confirmed_at', label: 'Confirmed at', value: this.formatDateTime(data.created_at) },
                { key: 'customer', label: 'Receiver', value: data.name },
              ],
            },
            notes: {
              title: data.key === 'customer' ? 'Notes' : 'Manufactur Notes',
              infoList: [
                { key: 'notes', label: 'Notes', value: get(data, 'metadata.notes', '-') },
              ],
            },
          };
          const images = get(data, 'metadata.images', []);

          if (!isEmpty(images)) {
            const imagesDt = images.map((image) => {
              const formattedImage = this.generateImgUrl(image);
              return formattedImage;
            });

            confirmationItem.notes.infoList.push({ key: 'images', value: imagesDt });
          }

          if (!isEmpty(data.signature)) {
            confirmationItem.confirmation.infoList.push({ key: 'signature', label: 'Signature', value: data.signature });
          }

          return confirmationItem;
        });
      }

      return confirmationList;
    },
    orderDetail() {
      const data = this.detailedInfo;
      const customer = data.customer || {};
      const hasCustomer = !isEmpty(customer);

      const invoiceNumber = data.invoice ? data.invoice.number : '-';
      const customerName = hasCustomer ? customer.name : '-';
      let customerCity = '-';

      if (hasCustomer) {
        const customerHasAddress = !isEmpty(customer.address);
        const customerAddress = customerHasAddress ? customer.address : '-';
        if (customerHasAddress) customerCity = customerAddress.city;
      }

      const infoList = [
        { key: 'order_number', label: 'Order Number', value: data.number },
        { key: 'invoice_number', label: 'Invoice Number', value: invoiceNumber },
        { key: 'customer', label: 'Customer', value: customerName },
        { key: 'customer_city', label: 'Customer City', value: customerCity },
        {
          key: 'created_at',
          label: 'Created at',
          value: this.formatDateDistance(data.created_at),
          title: this.formatDateTime(data.created_at),
        },
      ];

      return infoList;
    },
    notesData() {
      const data = this.detailedInfo.confirmations
        .find(c => c.key === 'customer');

      if (!data) { return []; }

      const metadata = get(data, 'metadata', {});
      let images = get(metadata, 'images', []);
      let notes = get(metadata, 'notes', '-');

      const { status } = this.detailedInfo;
      if (status === 'REJECTED') {
        const meta = get(this.detailedInfo, 'metadata', {});
        const rejected = get(meta, 'rejected', {});
        notes = get(rejected, 'reason', '-');
      }

      images = images.map((image) => {
        const formattedImage = this.generateImgUrl(image);
        return formattedImage;
      });

      const infoList = [
        { key: 'notes', label: 'Notes', value: notes },
        { key: 'images', value: images },
      ];

      return infoList;
    },
    destinationInfo() {
      const { metadata = {}, destination = {} } = this.detailedInfo;
      let { geometry } = destination;
      const { metadata: metaDestination = {} } = destination;
      const {
        latitude, longitude, name, code,
      } = destination;

      if (isNull(geometry) || isUndefined(geometry)) geometry = { type: '', coordinates: [[0, 0]] };

      const isTrue = true;
      let coordinates = [0, 0];
      let center = [0, 0];
      let marker = { lat: 0, lng: 0 };
      const radius = !isNull(metaDestination) ? metaDestination.radius : 200;
      const isCircle = !isNull(metaDestination) && geometry.type === 'Point' ? isTrue : !isTrue;
      let latlngs = geometry.coordinates;

      const firstCoordinate = get(latlngs, '0.0') || [0, 0];
      const inValidCoordinates = firstCoordinate[0] > 0;

      if (isCircle) {
        if (isNull(longitude) && isNull(latitude)) {
          center = geometry.coordinates;
          coordinates = { geometry: { coordinates } };
          marker = { lat: geometry.coordinates[0], lng: geometry.coordinates[1] };
        } else {
          center = [latitude, longitude];
          coordinates = [latitude, longitude];
          marker = { lat: latitude, lng: longitude };
        }
      } else {
        const polygon = L.polygon(geometry.coordinates);
        const getCenter = polygon.getBounds().getCenter();
        center = values(getCenter);
        marker = getCenter;
      }

      if (inValidCoordinates) {
        latlngs = latlngs.map((coordinatesInv) => {
          const switchedCoordinates = coordinatesInv.map(coordinate => (
            [coordinate[1], coordinate[0]]
          ));
          return switchedCoordinates;
        });

        const polygon = L.polygon(latlngs);
        center = polygon.getBounds().getCenter();
        center = values(center);
        marker = center;
      }

      const mapValue = {
        marker, center, name, coordinates, radius, latlngs, isCircle,
      };

      const timeIn = this.formatDateTime(this.getTimeIn(metadata));
      const timeOut = this.formatDateTime(this.getTimeOut(metadata));

      const infoList = [
        { key: 'code', label: 'Code', value: code },
        { key: 'name', label: 'Name', value: name },
        { key: 'map', label: 'Map', value: mapValue },
      ];

      if (timeIn !== 'Invalid Date') infoList.splice(2, 0, { key: 'timeIn', label: 'Time In', value: timeIn });
      if (timeOut !== 'Invalid Date') infoList.splice(3, 0, { key: 'timeOut', label: 'Time Out', value: timeOut });

      return infoList;
    },
    confirmed() {
      const { confirmations = {}, metadata = {} } = this.detailedInfo;
      const isConfirmed = find(confirmations, dt => (dt.key === 'customer' || dt.key === 'manufacturer'));
      const isRejected = !isEmpty(get(metadata, 'rejected'));
      return isConfirmed || isRejected;
    },
    validDestination() {
      return !isEmpty(this.detailedInfo.destination);
    },
  },
  watch: {
    detailedInfo(data) {
      const customer = data.customer || {};
      const hasCustomer = !isEmpty(customer);

      const invoiceNumber = data.invoice ? data.invoice.number : '-';
      const customerName = hasCustomer ? customer.name : '-';
      let customerCity = '-';

      if (hasCustomer) {
        const customerHasAddress = !isEmpty(customer.address);
        const customerAddress = customerHasAddress ? customer.address : '-';
        if (customerHasAddress) customerCity = customerAddress.city;
      }

      const infoList = [
        { key: 'order_number', label: 'Order Number', value: data.number },
        { key: 'invoice_number', label: 'Invoice Number', value: invoiceNumber },
        { key: 'customer', label: 'Customer', value: customerName },
        { key: 'customer_city', label: 'Customer City', value: customerCity },
        {
          key: 'created_at',
          label: 'Created at',
          value: this.formatDateDistance(data.created_at),
          title: this.formatDateTime(data.created_at),
        },
      ];

      this.detailData = infoList;
    },
  },
  methods: {
    uniqueId(key) {
      return uniqueId(key);
    },
    createSignature(data) {
      return `data:image/png;base64,${data}`;
    },
    getCenter(polygon) {
      return polygon.reduce((x, y) =>
        [x[0] + (y[0] / polygon.length), x[1] + (y[1] / polygon.length)], [0, 0]);
    },
    showHideMap() {
      this.showMap = !this.showMap;
      if (this.showMap && !this.setInvalidateSize) {
        setTimeout(() => { this.mapObject.invalidateSize(); }, 5e2);
        this.setInvalidateSize = !this.setInvalidateSize;
      }
    },
    getTimeIn(metadata) {
      if (isObject(metadata)) {
        return metadata.time_in || metadata.time_in_app;
      }
      return undefined;
    },
    getTimeOut(metadata) {
      if (isObject(metadata)) {
        return metadata.time_out || metadata.time_out_app;
      }
      return undefined;
    },
    generateImgUrl(file) {
      const imgUrl = `${get(Config, 'default.tmsGraphqlApiUrl')}/uploads/images/${file}`;
      return imgUrl;
    },
    showThumbnail(images) {
      this.$viewerApi({ images });
    },
  },
};
</script>
<style lang="sass">
@import '~viewerjs/dist/viewer.css'

.confirm-signature
  max-height: 113px;
.img-notes
  height: 100%;
  width: 100%;
  cursor: pointer;
.carousel-inner
  height: 159px;
</style>



// WEBPACK FOOTER //
// src/views/pages/shipment_md/orders/detail/Index.vue